import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import type { ApiResponse } from '../types/common';

// Ensure we have a complete URL with protocol
const getApiUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) return envUrl;
};

const API_URL = getApiUrl();

const api = axios.create({
  baseURL: API_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add authentication token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const apiClient = {
  get: async <T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    try {
      const response = await api.get(url, config);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Network error' };
    }
  },

  post: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    try {
      const response = await api.post(url, data, config);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Network error' };
    }
  },

  patch: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    try {
      const response = await api.patch(url, data, config);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Network error' };
    }
  },

  delete: async <T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    try {
      const response = await api.delete(url, config);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Network error' };
    }
  },

  uploadFiles: async <T = any>(url: string, formData: FormData): Promise<ApiResponse<T>> => {
    try {
      const response = await api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || { success: false, message: 'Upload error' };
    }
  }
};

export const getImageUrl = (filename: string): string => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (!envUrl) throw new Error('VITE_API_URL is not set in .env');
  const baseUrl = envUrl.replace('/api', '');
  return `${baseUrl}/uploads/${filename}`;
};

export default api;
